'use es6';

import PropTypes from 'prop-types';
import { PureComponent } from 'react';
import { NavMarker } from 'react-rhumb';
import Immutable from 'immutable';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UITable from 'UIComponents/table/UITable';
import UIPaginator from 'UIComponents/paginator/UIPaginator';
import UISortTH from 'UIComponents/table/UISortTH';
import UITableActionsRow from 'UIComponents/table/UITableActionsRow';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import UICheckbox from 'UIComponents/input/UICheckbox';
import UILoadingOverlay from 'UIComponents/loading/UILoadingOverlay';
import { RequestStatus } from 'FileManagerCore/Constants';
import { isFile } from 'FileManagerCore/utils/FoldersAndFiles';
import FileFolderTableRow from './FileFolderTableRow';
import EmptySearchResults from './EmptySearchResults';
import LoadingFailed from 'FileManagerCore/components/LoadingFailed';
import BulkActions from './bulkActions';
import { MAX_SELECTABLE_ITEMS, PAGE_SIZES } from '../Constants';
import * as SortDirection from '../enums/SortDirection';
import AsyncDetailPanel from './codeSplitting/AsyncDetailPanel';
import { ObjectCategory } from 'FileManagerCore/Constants';
import UIDropdown from 'UIComponents/dropdown/UIDropdown';
import UIList from 'UIComponents/list/UIList';
import UIButton from 'UIComponents/button/UIButton';
import { MAX_OBJECT_REQUEST_LIMIT } from 'FileManagerCore/constants/CrmPlatformLimits';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
export default class FileFolderTable extends PureComponent {
  constructor() {
    super();
    this.handleKeyCommands = event => {
      const {
        selectedFile,
        onSelectFile,
        objects
      } = this.props;
      if (event.metaKey || event.ctrlKey || !['ArrowDown', 'ArrowUp'].includes(event.key)) {
        return;
      }
      if (event.target !== document.body) {
        return;
      }
      const files = objects.filter(isFile);
      if (!selectedFile || files.size <= 1) {
        return;
      }
      const index = files.findIndex(object => object.get('id') === selectedFile.get('id'));
      switch (event.key) {
        case 'ArrowDown':
          {
            if (index < files.size - 1) {
              event.preventDefault();
              onSelectFile(files.get(index + 1));
            }
            break;
          }
        case 'ArrowUp':
          {
            if (index > 0) {
              event.preventDefault();
              onSelectFile(files.get(index - 1));
            }
            break;
          }
        default:
      }
    };
    this.handlePanelClose = () => {
      const {
        onSelectFile
      } = this.props;
      onSelectFile(null);
    };
    this.handlePageChange = event => {
      const {
        onPageChange
      } = this.props;
      onPageChange(event.target.value);
    };
    this.handleSelectAllToggle = event => {
      const {
        objects,
        onAddFileBulkSelection,
        onAddFolderBulkSelection,
        onClearBulkSelection
      } = this.props;
      const fileIds = objects.filter(o => o.get('category') === ObjectCategory.FILE).map(o => o.get('id')).toArray();
      const folderIds = objects.filter(o => o.get('category') === ObjectCategory.FOLDER).map(o => o.get('id')).toArray();
      if (event.target.checked) {
        onAddFileBulkSelection(fileIds);
        onAddFolderBulkSelection(folderIds);
      } else {
        onClearBulkSelection();
      }
    };
    this.handleAddBulkSelection = file => {
      const {
        onAddFileBulkSelection,
        onAddFolderBulkSelection
      } = this.props;
      if (file.get('category') === ObjectCategory.FILE) {
        onAddFileBulkSelection([file.get('id')]);
      } else {
        onAddFolderBulkSelection([file.get('id')]);
      }
    };
    this.handleRemoveBulkSelection = file => {
      const {
        onRemoveFileBulkSelection,
        onRemoveFolderBulkSelection
      } = this.props;
      if (file.get('category') === ObjectCategory.FILE) {
        onRemoveFileBulkSelection([file.get('id')]);
      } else {
        onRemoveFolderBulkSelection([file.get('id')]);
      }
    };
    this.handleBulkSelectFile = (file, action = 'add') => {
      if (action === 'add') {
        this.handleAddBulkSelection(file);
      } else {
        this.handleRemoveBulkSelection(file);
      }
    };
    this.renderRow = object => {
      const {
        onDropFiles,
        onSelectFile,
        selectedFile,
        bulkSelectedObjectIds
      } = this.props;
      const selected = !!selectedFile && selectedFile.get('id') === object.get('id');
      return /*#__PURE__*/_jsx(FileFolderTableRow, {
        object: object,
        selected: selected,
        reachedSelectionLimit: this.hasReachedBulkSelectionLimit(),
        bulkSelected: bulkSelectedObjectIds.has(object.get('id')),
        onSelectFile: onSelectFile,
        onBulkSelectFile: this.handleBulkSelectFile,
        onDropFiles: onDropFiles
      }, object.get('id'));
    };
    this.handleSortByNameChange = this.handleSortChange.bind(this, 'name');
    this.handleSortByUpdatedChange = this.handleSortChange.bind(this, 'updated');
    this.handleSortBySizeChange = this.handleSortChange.bind(this, 'size');
    this.handleSortByTypeChange = this.handleSortChange.bind(this, 'extension');
  }
  componentDidMount() {
    window.addEventListener('keydown', this.handleKeyCommands, true);
  }
  componentWillUnmount() {
    window.removeEventListener('keydown', this.handleKeyCommands, true);
  }
  getI18nKey(suffix) {
    return `FileManagerDashboard.columns.${suffix}`;
  }
  getDisplayedPageCount() {
    const {
      total,
      pageSize,
      page
    } = this.props;
    if (total === 0) {
      return 0;
    }
    const totalPages = Math.ceil(total / pageSize);
    const totalPagesPossibleAtOnce = Math.ceil(MAX_OBJECT_REQUEST_LIMIT / pageSize);
    return Math.min(page + totalPagesPossibleAtOnce, totalPages);
  }
  getSelectedObjectIds() {
    const {
      bulkSelectedObjectIds,
      objects
    } = this.props;
    return objects.filter(o => bulkSelectedObjectIds.has(o.get('id'))).map(o => o.get('id'));
  }
  isEntirePageSelected() {
    const {
      bulkSelectedObjectIds,
      objects
    } = this.props;
    return !bulkSelectedObjectIds.isEmpty() && this.getSelectedObjectIds().size === objects.size;
  }
  hasPartialPageSelection() {
    return !this.getSelectedObjectIds().isEmpty() && !this.isEntirePageSelected();
  }
  hasReachedBulkSelectionLimit() {
    const {
      bulkSelectedObjectIds
    } = this.props;
    return bulkSelectedObjectIds.size >= MAX_SELECTABLE_ITEMS;
  }
  getShouldRenderEmptySearchResults() {
    const {
      status,
      objects,
      searchText,
      fileTypeQuery
    } = this.props;
    return status === RequestStatus.SUCCEEDED && objects.size === 0 && (searchText && searchText.length > 0 || fileTypeQuery);
  }
  handleSortChange(orderBy, event) {
    const {
      onSortChange
    } = this.props;
    onSortChange({
      sortDirection: event.target.value,
      orderBy
    });
  }
  renderRows() {
    return this.props.objects.map(this.renderRow).toArray();
  }
  renderPagination() {
    const {
      status,
      page,
      total,
      fetchingMoreFiles,
      pageSize
    } = this.props;
    const pageCount = this.getDisplayedPageCount();
    if (!(pageCount > 1 && status === RequestStatus.SUCCEEDED)) {
      return null;
    }
    const lastPageTooFar = total >= MAX_OBJECT_REQUEST_LIMIT;
    // cover pagination in an overlay preventing more clicks while fetching more files upon pagination
    return /*#__PURE__*/_jsxs("div", {
      className: "pagination-wrapper",
      children: [fetchingMoreFiles && /*#__PURE__*/_jsx(UILoadingOverlay, {
        contextual: true,
        minHeight: 40
      }), /*#__PURE__*/_jsx(UIPaginator, {
        onPageChange: this.handlePageChange,
        page: page,
        pageCount: pageCount,
        showFirstLastButtons: !lastPageTooFar
      }), /*#__PURE__*/_jsx(UIDropdown, {
        buttonUse: "transparent",
        buttonText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "FileManagerDashboard.pageSize",
          options: {
            size: pageSize
          }
        }),
        "data-test-id": "page-select-dropdown",
        buttonClassName: "m-left-4",
        children: /*#__PURE__*/_jsx(UIList, {
          children: PAGE_SIZES.map(size => /*#__PURE__*/_jsx(UIButton, {
            "data-test-id": `page-size-${size}`,
            onClick: () => this.props.onSetPageSize(size),
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "FileManagerDashboard.pageSize",
              options: {
                size
              },
              style: {
                fontWeight: size === pageSize ? 'bold' : 'normal'
              },
              "data-test-id": `page-select-dropdown-${size}`
            })
          }, `page-size-${size}`))
        })
      })]
    });
  }
  render() {
    const {
      orderBy,
      sortDirection,
      status,
      selectedFile,
      bulkSelectedObjectIds,
      areFilesUploading
    } = this.props;
    const isLoading = [RequestStatus.PENDING, RequestStatus.UNINITIALIZED].includes(status);
    return /*#__PURE__*/_jsxs("div", {
      className: "m-bottom-4 notranslate",
      children: [/*#__PURE__*/_jsxs(UITable, {
        className: 'file-folder-table' + (!isLoading ? " loaded" : ""),
        responsive: false,
        children: [/*#__PURE__*/_jsxs("colgroup", {
          children: [/*#__PURE__*/_jsx("col", {
            width: 50
          }), /*#__PURE__*/_jsx("col", {}), /*#__PURE__*/_jsx("col", {
            width: 180
          }), /*#__PURE__*/_jsx("col", {
            width: 120
          }), /*#__PURE__*/_jsx("col", {
            width: 100
          }), /*#__PURE__*/_jsx("col", {
            width: 180
          })]
        }), /*#__PURE__*/_jsx("thead", {
          children: /*#__PURE__*/_jsxs(UITableActionsRow, {
            open: !bulkSelectedObjectIds.isEmpty(),
            openAtColumnIndex: 1,
            actions: /*#__PURE__*/_jsx(BulkActions, {
              selectedCount: bulkSelectedObjectIds.size
            }),
            children: [/*#__PURE__*/_jsx("th", {
              className: "p-all-0",
              children: /*#__PURE__*/_jsx(UICheckbox, {
                "data-test-id": "file-folder-table-checkbox",
                className: "m-x-auto",
                onChange: this.handleSelectAllToggle,
                checked: this.isEntirePageSelected(),
                indeterminate: this.hasPartialPageSelection(),
                disabled: this.hasReachedBulkSelectionLimit() && !this.isEntirePageSelected(),
                "aria-label": I18n.text('FileManagerDashboard.actions.selectAll')
              })
            }), /*#__PURE__*/_jsx(UISortTH, {
              onSortChange: this.handleSortByNameChange,
              preferredSort: SortDirection.ASC,
              sort: orderBy === 'name' ? sortDirection : 'none',
              disabled: areFilesUploading,
              children: I18n.text('FileManagerDashboard.columns.name')
            }), /*#__PURE__*/_jsx("th", {
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: this.getI18nKey('fileVisibility')
              })
            }), /*#__PURE__*/_jsx(UISortTH, {
              onSortChange: this.handleSortBySizeChange,
              preferredSort: SortDirection.ASC,
              sort: orderBy === 'size' ? sortDirection : 'none',
              disabled: areFilesUploading,
              children: I18n.text('FileManagerDashboard.columns.size')
            }), /*#__PURE__*/_jsx(UISortTH, {
              onSortChange: this.handleSortByTypeChange,
              preferredSort: SortDirection.ASC,
              sort: orderBy === 'extension' ? sortDirection : 'none',
              disabled: areFilesUploading,
              children: I18n.text('FileManagerDashboard.columns.type')
            }), /*#__PURE__*/_jsx(UISortTH, {
              onSortChange: this.handleSortByUpdatedChange,
              preferredSort: SortDirection.ASC,
              sort: orderBy === 'updated' ? sortDirection : 'none',
              "data-test-id": "sort-by-updated",
              disabled: areFilesUploading,
              children: I18n.text('FileManagerDashboard.columns.updated')
            })]
          })
        }), /*#__PURE__*/_jsx("tbody", {
          children: status === RequestStatus.SUCCEEDED && this.renderRows()
        })]
      }), isLoading && /*#__PURE__*/_jsx(UILoadingSpinner, {
        "data-test-id": "file-folder-table-loading",
        grow: true,
        minHeight: 720
      }), this.getShouldRenderEmptySearchResults() && /*#__PURE__*/_jsx(EmptySearchResults, {}), status === RequestStatus.FAILED && /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(LoadingFailed, {}), /*#__PURE__*/_jsx(NavMarker, {
          name: "FILE_FOLDER_TABLE_FAILED"
        })]
      }), selectedFile && /*#__PURE__*/_jsx(AsyncDetailPanel, {
        file: selectedFile,
        onClose: this.handlePanelClose
      }, selectedFile.get('id')), this.renderPagination(), status === RequestStatus.SUCCEEDED && /*#__PURE__*/_jsx(NavMarker, {
        name: "FILE_FOLDER_TABLE_LOADED"
      })]
    });
  }
}
FileFolderTable.propTypes = {
  orderBy: PropTypes.string.isRequired,
  sortDirection: PropTypes.oneOf(Object.values(SortDirection)).isRequired,
  objects: PropTypes.instanceOf(Immutable.List),
  searchText: PropTypes.string,
  fileTypeQuery: PropTypes.string,
  page: PropTypes.number.isRequired,
  total: PropTypes.number.isRequired,
  status: PropTypes.oneOf(Object.keys(RequestStatus)).isRequired,
  fetchingMoreFiles: PropTypes.bool.isRequired,
  bulkSelectedObjectIds: PropTypes.instanceOf(Immutable.Set).isRequired,
  selectedFile: PropTypes.instanceOf(Immutable.Map),
  onPageChange: PropTypes.func.isRequired,
  onSelectFile: PropTypes.func.isRequired,
  onAddFileBulkSelection: PropTypes.func.isRequired,
  onAddFolderBulkSelection: PropTypes.func.isRequired,
  onRemoveFileBulkSelection: PropTypes.func.isRequired,
  onRemoveFolderBulkSelection: PropTypes.func.isRequired,
  onClearBulkSelection: PropTypes.func.isRequired,
  onSortChange: PropTypes.func.isRequired,
  onDropFiles: PropTypes.func.isRequired,
  onSetPageSize: PropTypes.func.isRequired,
  pageSize: PropTypes.number.isRequired,
  areFilesUploading: PropTypes.bool
};